import { AxiosResponse } from 'axios';
import { PayloadAction } from '@reduxjs/toolkit';
import { call, put, takeLatest } from 'redux-saga/effects';
import wooCommerceApi from './api';
import { searchCatalog } from './catalogSearch';
import {
  fetchProductsFailure,
  fetchProductsRequest,
  fetchProductsSuccess,
  fetchProductBySlugFailure,
  fetchProductBySlugRequest,
  fetchProductBySlugSuccess,
  FetchProductBySlugPayload,
  FetchProductsRequestPayload,
} from './productSlice';
import { Product, WooCategory } from './types';

const DEFAULT_PRODUCTS_PER_PAGE = 12;
const MAX_PRODUCTS_PER_PAGE = 24;

type ProductListResponse = AxiosResponse<Product[]>;
// type CategoryListResponse = AxiosResponse<WooCategory[]>;

type FetchParams = {
  page: number;
  perPage: number;
  productSlug?: string;
  categoryId?: number;
  tagId?: number;
  onSaleOnly?: boolean;
  searchQuery?: string;
};

async function getCategoryIdBySlug(slug: string): Promise<number | undefined> {
  try {
    const response = await wooCommerceApi.get<WooCategory[]>('/products/categories', {
      params: { slug: slug.toLowerCase() }
    });
    return response.data?.[0]?.id;
  } catch (error) {
    return undefined;
  }
}

function fetchProductsPageApi({ page, perPage, productSlug, categoryId, tagId, onSaleOnly, searchQuery }: FetchParams): Promise<ProductListResponse> {
  const params: any = {
    per_page: perPage,
    status: 'publish',
    page,
  };

  if (productSlug) params.slug = productSlug;
  if (categoryId) params.category = categoryId;
  if (tagId) params.tag = tagId;
  if (onSaleOnly) params.on_sale = true;
  if (searchQuery?.trim()) params.search = searchQuery.trim();

  return wooCommerceApi.get<Product[]>('/products', { params });
}

export function* fetchProductsWorker(action: PayloadAction<FetchProductsRequestPayload | undefined>) {
  const requestedCategorySlug = action.payload?.categorySlug;
  const onSaleOnly = Boolean(action.payload?.onSaleOnly);
  const searchQuery = action.payload?.searchQuery;
  const page = Math.max(1, action.payload?.page || 1);
  const requestedPerPage = action.payload?.perPage || DEFAULT_PRODUCTS_PER_PAGE;
  const perPage = Math.min(Math.max(1, requestedPerPage), MAX_PRODUCTS_PER_PAGE);

  try {
    let categoryId: number | undefined;
    if (requestedCategorySlug) {
      categoryId = yield call(getCategoryIdBySlug, requestedCategorySlug);
      if (!categoryId) throw new Error('Unable to load the selected category. Please try again.');
    }

    if (searchQuery?.trim()) {
      const result: Awaited<ReturnType<typeof searchCatalog>> = yield call(searchCatalog, searchQuery, page, perPage, categoryId, onSaleOnly);
      yield put(fetchProductsSuccess({ ...result, page, append: action.payload?.append }));
      return;
    }

    const response: ProductListResponse = yield call(fetchProductsPageApi, {
      page,
      perPage,
      categoryId,
      onSaleOnly,
      searchQuery,
    });
    const totalPagesHeader = Number(response.headers?.['x-wp-totalpages']);
    const totalPages = Number.isFinite(totalPagesHeader) && totalPagesHeader > 0 ? totalPagesHeader : page;
    const totalItemsHeader = Number(response.headers?.['x-wp-total']);
    const totalItems = Number.isFinite(totalItemsHeader) && totalItemsHeader >= 0
      ? totalItemsHeader
      : (response.data || []).length;
    const items = response.data || [];

    yield put(
      fetchProductsSuccess({
        items,
        page,
        totalPages,
        totalItems,
        append: action.payload?.append,
      }),
    );
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch products';
    yield put(fetchProductsFailure(errorMessage));
  }
}

export function* fetchProductBySlugWorker(action: PayloadAction<FetchProductBySlugPayload>) {
  try {
    const response: ProductListResponse = yield call(fetchProductsPageApi, {
      page: 1,
      perPage: 1,
      productSlug: action.payload.slug,
    });
    const product = response.data?.[0];

    if (!product) {
      yield put(fetchProductBySlugFailure('Product not found'));
      return;
    }

    yield put(fetchProductBySlugSuccess(product));
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch product';
    yield put(fetchProductBySlugFailure(errorMessage));
  }
}

export function* watchProductRequests() {
  yield takeLatest(fetchProductsRequest.type, fetchProductsWorker);
  yield takeLatest(fetchProductBySlugRequest.type, fetchProductBySlugWorker);
}
